<script setup lang="ts">
import type { Form, FormSubmitEvent } from '#ui/types';
import { useMutation } from '@tanstack/vue-query';
import { z } from 'zod';

import {
    apiSendSignupOtp,
    apiVerifySignupOtp,
    BackendErrorCode,
    type BackendErrorEntity,
    type BodyType,
    type ErrorType,
    type SendOtpBodyDto,
    type VerifyOtpBodyDto,
} from '~/apiClient';
import { AUTOCOMPLETE_OFF } from '~/constants/form';

interface VerifyOtpFormProps {
    email: string;
    requestOtp?: boolean;
}

// PROPS
const { email, requestOtp } = defineProps<VerifyOtpFormProps>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { logError } = useLogs();
const { notifyBackendError, notifyGenericError, notifyError, notifySuccess } = useNotification();
const { turnstileContainerId, getToken, formatRequestOptions } = useInteractionOnlyTurnstile('verifyOtp');
const { mutateAsync: mutateVerify } = useMutation<
    Awaited<ReturnType<typeof apiVerifySignupOtp>>,
    ErrorType<BackendErrorEntity>,
    { data: BodyType<VerifyOtpBodyDto>; token: string | null },
    unknown
>({
    mutationFn: ({ data, token }) => apiVerifySignupOtp(data, formatRequestOptions(token)),
});
const { mutateAsync: mutateSend, isPending: isPendingSend } = useMutation<
    Awaited<ReturnType<typeof apiSendSignupOtp>>,
    ErrorType<BackendErrorEntity>,
    { data: BodyType<SendOtpBodyDto>; token: string | null },
    unknown
>({
    mutationFn: ({ data, token }) => apiSendSignupOtp(data, formatRequestOptions(token)),
});
const { data: session, getSession, state: authState } = useAuthUtils();
const { trackSignupSuccess } = useTracking();
const { resendCooldown, isResendDisabled, startResendCooldown } = useResendCooldown();

// EMITS
const emit = defineEmits(['success']);

// DATA
const isPendingVerify = ref<boolean>(false);
const isSubmitting = computed(() => isPendingVerify.value || isPendingSend.value);

const formSchema = z.object({
    email: z.email(tt('validation.msg.invalid', { field: tt('login.email') })),
    otp: z
        .array(z.string().length(1, tt('validation.msg.required', { field: tt('signup.verify.otp') })))
        .length(6, tt('validation.msg.invalid', { field: tt('signup.verify.otp') })),
});
type FormSchema = z.infer<typeof formSchema>;
const formData = reactive<FormSchema>({
    email,
    otp: getEmptyOtp(),
});
const form = useTemplateRef<Form<FormSchema>>('form');

// HANDLERS
const onSubmit = async (event: FormSubmitEvent<FormSchema>): Promise<void> => {
    isPendingVerify.value = true;

    let turnstileToken: string | null = null;

    try {
        turnstileToken = await getToken();
    } catch {
        notifyError({
            title: tt('validation.msg.captcha'),
            description: tt('notifications.tryAgain'),
        });

        return;
    }

    await mutateVerify({
        data: {
            email,
            otp: event.data.otp.join(''),
        },
        token: turnstileToken,
    })
        .then(async ({ token }) => {
            authState.setToken(token);
            await getSession();
            session.value?.user && trackSignupSuccess(session.value.user);
            emit('success');
        })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error verifying account with OTP', { error: apiError, email });

            if (isFetchError(apiError) && isBackendError(apiError.data)) {
                if ([BackendErrorCode.OTPCodeNotValid, BackendErrorCode.OtpExpiredOrNotFound].includes(apiError.data.error.code)) {
                    form.value?.setErrors([{ name: 'otp', message: tt('validation.msg.invalid', { field: tt('signup.verify.otp') }) }]);
                }

                if (apiError.data.error.code === BackendErrorCode.OtpResendTooSoon) {
                    startResendCooldown();
                }

                notifyBackendError({ error: apiError.data.error });

                return;
            }

            notifyGenericError();
        });

    isPendingVerify.value = false;
};

const onResend = async (): Promise<void> => {
    let turnstileToken: string | null = null;

    try {
        turnstileToken = await getToken();
    } catch {
        notifyError({
            title: tt('validation.msg.captcha'),
            description: tt('notifications.tryAgain'),
        });

        return;
    }

    await mutateSend({ data: { email }, token: turnstileToken })
        .then(() => {
            formData.otp = ['', '', '', '', '', ''];
            form.value?.clear();
            startResendCooldown();
            notifySuccess({
                title: tt('signup.verify.resend.success.title'),
                description: tt('signup.verify.resend.success.description'),
                icon: 'lucide:mail-check',
            });
        })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error requesting new OTP', { error: apiError, email });

            if (!isBackendError(apiError.data)) {
                notifyGenericError();

                return;
            }

            const { error } = apiError.data;
            notifyBackendError({ error });
        });
};

// LIFECYCLE
onMounted(() => {
    if (requestOtp) {
        onResend();
    } else {
        startResendCooldown();
    }
});
</script>

<template>
    <UForm ref="form" class="flex w-full flex-col gap-4" :state="formData" :schema="formSchema" @submit="onSubmit">
        <p class="text-muted text-center font-medium">
            {{ tt('signup.verify.subtitle', { channel: tt('signup.verify.resend.email') }) }}
        </p>

        <UFormField :label="tt('signup.fields.email.base')" name="email" required>
            <UInput v-model.trim="formData.email" disabled />
        </UFormField>
        <UFormField :label="tt('signup.verify.otp')" name="otp" required>
            <UPinInput v-model="formData.otp" :disabled="isSubmitting" :autocomplete="AUTOCOMPLETE_OFF" :length="6" otp autofocus />
        </UFormField>

        <UButton :label="tt('signup.verify.confirmCta')" type="submit" size="xl" :disabled="isPendingSend" :loading="isPendingVerify" block />

        <UButton
            color="neutral"
            variant="outline"
            :label="tt('signup.verify.resend.cta')"
            size="xl"
            block
            :disabled="isResendDisabled || isPendingVerify"
            :loading="isPendingSend"
            @click="onResend"
        >
            <template v-if="resendCooldown" #trailing>{{ resendCooldown }}</template>
        </UButton>

        <TurnstileChallenge :container-id="turnstileContainerId" />
    </UForm>
</template>
